import { useMemo, useState } from "react";
import { History } from "lucide-react";
import { useDietHistory, useWorkoutHistory } from "@/lib/queries";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { HistoryTimeline } from "@/components/evolucao/HistoryTimeline";
import {
  WindowFilter,
  windowDays,
  type WindowKey,
} from "@/components/evolucao/WindowFilter";

/**
 * Histórico do aluno — portado de
 * `frontend/components/student-portal/HistoryTimeline.tsx`. Timeline unificada
 * de treinos (useWorkoutHistory) + refeições (useDietHistory), ordenada por
 * data desc, com filtro de janela 7d/30d/90d (client-side sobre os 90 dias
 * carregados).
 */

export function HistoricoView() {
  const [filter, setFilter] = useState<WindowKey>("30d");
  const workouts = useWorkoutHistory(90);
  const diets = useDietHistory(90);

  const isLoading = workouts.isLoading || diets.isLoading;

  const cutoff = useMemo(
    () => Date.now() - windowDays(filter) * 24 * 60 * 60 * 1000,
    [filter],
  );

  const filteredWorkouts = useMemo(
    () =>
      (workouts.data?.executions ?? []).filter(
        (e) => Date.parse(e.executed_at) >= cutoff,
      ),
    [workouts.data, cutoff],
  );

  const filteredDiets = useMemo(
    () =>
      (diets.data?.executions ?? []).filter(
        (e) => Date.parse(e.executed_at) >= cutoff,
      ),
    [diets.data, cutoff],
  );

  const isEmpty = filteredWorkouts.length === 0 && filteredDiets.length === 0;

  return (
    <div className="animate-fade-in px-5 pt-8">
      <header className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
          Seus registros
        </p>
        <h1 className="font-display text-xl text-ink">Histórico</h1>
      </header>

      {isLoading ? (
        <Spinner fullscreen />
      ) : (
        <div className="space-y-4">
          <WindowFilter value={filter} onChange={setFilter} />

          {isEmpty ? (
            <EmptyState
              icon={History}
              title="Nada por aqui ainda"
              description="Quando você registrar um treino ou uma refeição, aparece aqui na linha do tempo."
            />
          ) : (
            <HistoryTimeline
              executions={filteredWorkouts}
              dietExecutions={filteredDiets}
            />
          )}
        </div>
      )}
    </div>
  );
}
